import type { CSSProperties, ReactNode } from 'react';
import { useEffect } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import CenterModal from '@shared/modal/CenterModal';
import { UMR } from '../../urbanmap/_components';
import type { PlanStatus } from './types';

export const fmt = (n: number, digits = 0) => n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits });

export const WARN = 'var(--nj-core-color-orange-800, #8a3c14)';

export function SaveBar({ dirty, readOnly, onCancel, onSave }: { dirty: boolean; readOnly?: boolean; onCancel: () => void; onSave: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
      {dirty && <span style={{ fontSize: 12, color: WARN, marginRight: 'auto' }}>Modifications non confirmées</span>}
      {readOnly && <span style={{ fontSize: 12, color: UMR.textSecondary, marginRight: 'auto' }}>Lecture seule pendant le calcul</span>}
      <NJButton label="Annuler" variant="secondary" emphasis="subtle" disabled={!dirty} onClick={onCancel} />
      <NJButton label="Confirmer" variant="primary" disabled={!dirty || readOnly} onClick={onSave} />
    </div>
  );
}

export const Tag = ({ children, v2 }: { children: ReactNode; v2?: boolean }) => (
  <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 10, background: v2 ? 'var(--nj-core-color-grey-100, #f2f2f2)' : 'var(--nj-core-color-teal-100, #e3f4f2)', color: v2 ? UMR.textSecondary : ACCENT, whiteSpace: 'nowrap' }}>{children}</span>
);

export const STATUS: Record<PlanStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Brouillon', color: 'var(--nj-core-color-grey-700, #555)', bg: 'var(--nj-core-color-grey-100, #f2f2f2)' },
  running: { label: 'Calcul en cours', color: 'var(--nj-core-color-blue-800, #0b4a7c)', bg: 'var(--nj-core-color-blue-100, #e6f3fb)' },
  done: { label: 'Calculé', color: 'var(--nj-core-color-green-800, #1b6b30)', bg: 'var(--nj-core-color-green-100, #e6f5ea)' },
  stale: { label: 'Obsolète', color: 'var(--nj-core-color-orange-800, #8a3c14)', bg: 'var(--nj-core-color-orange-100, #fdf0e7)' },
  failed: { label: 'En échec', color: 'var(--nj-semantic-color-text-error)', bg: 'var(--nj-semantic-color-background-error)' },
};

export function StatusPill({ status, progress }: { status: PlanStatus; progress?: number }) {
  const s = STATUS[status];
  const style: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 12, fontSize: 12, lineHeight: '18px',
    background: s.bg, color: s.color, whiteSpace: 'nowrap', fontFamily: UMR.font,
  };
  return (
    <span style={style}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
      {s.label}{status === 'running' && progress !== undefined ? ` ${progress} %` : ''}
    </span>
  );
}

export const ACCENT = 'var(--nj-core-color-teal-700, #00726d)';

/** Bandeau de traçabilité : rappelle la chaîne Projet → Hypothèses → Plan → Résultat. */
export function ChainBanner({ step }: { step: 'hypotheses' | 'plans' | 'params' }) {
  const steps: { key: string; label: string; active: boolean }[] = [
    { key: 'p', label: 'Projet', active: step === 'params' },
    { key: 'h', label: 'Hypothèses', active: step === 'hypotheses' },
    { key: 'd', label: 'Plan de dev', active: step === 'plans' },
    { key: 'r', label: 'Résultat', active: step === 'plans' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: UMR.textSecondary, flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {i > 0 && <span aria-hidden>→</span>}
          <span style={{ padding: '2px 8px', borderRadius: 10, fontWeight: s.active ? 700 : 400, background: s.active ? 'var(--nj-core-color-teal-100, #e3f4f2)' : 'var(--nj-core-color-grey-100, #f2f2f2)', color: s.active ? ACCENT : UMR.textSecondary }}>{s.label}</span>
        </span>
      ))}
    </div>
  );
}

export function ConfirmDialog({ title, children, onStay, onDiscard, onSave }: {
  title: string; children: ReactNode; onStay: () => void; onDiscard: () => void; onSave: () => void;
}) {
  useEffect(() => {
    const element = document.getElementById('navigation-confirm');
    if (!element) return;
    element.classList.add('show');
    element.style.display = 'block';
    element.style.background = 'rgba(0,0,0,0.4)';
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'true');
    element.setAttribute('aria-label', title);
  }, [title]);
  return <CenterModal title={title} dataTarget="navigation-confirm" onClick={onStay}
    body={<div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}><NJText scale="md">{children}</NJText></div>}
    footer={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <NJButton label="Rester" variant="secondary" emphasis="subtle" onClick={onStay} />
          <NJButton label="Abandonner les modifications" variant="destructive" emphasis="subtle" onClick={onDiscard} />
          <NJButton label="Confirmer les modifications" variant="primary" onClick={onSave} />
    </div>} />;
}
